/**
 * useHintRevealAnimation Hook
 *
 * State machine for button slide-out animations:
 *
 *   Simple slide (hints, lockout):
 *     idle → pre-pushing → dropping → post-drop → done (finish() skips grow)
 *
 *   Guess button (wait for dialog):
 *     idle → pushed-waiting (button shrinks, strobe plays)
 *       → continueDrop() → un-pushing → pre-pushing → dropping → post-drop → done
 *       → cancelPush()   → un-pushing → idle
 *
 * Pre-push:       Renders clip container (one paint frame before drop)
 * Pushed-waiting: Button shrunk with strobe while guess dialog is open
 * Un-pushing:     Button springs back to full size (CSS transition)
 * Drop:           Button translateY slides it out of the clip container
 * Post-drop:      Immediately finished by ClueCell's skip-grow effect
 *
 * Uses CSS transitions with onTransitionEnd for step completion.
 * pre-pushing auto-advances to dropping after one paint via double rAF.
 */

import { useState, useCallback, useEffect, useRef } from 'react';

// Configurable animation parameters
export const PUSH_DURATION = 600;  // ms — button push-in (3× for debug)
export const DROP_DURATION = 900;  // ms — button drop through slot (3× for debug)
export const POST_DROP_DELAY = 300; // ms — pause after drop before closing slot (3× for debug)
export const PRE_GROW_DELAY = 300;  // ms — pause after slot close before grow (3× for debug)
export const GROW_DURATION = 900;  // ms — clue lozenge grow to full size (3× for debug)
export const UN_PUSH_DURATION = 1500; // ms — button un-push (3× for debug)

// Size constants
export const FULL_WIDTH = 196;
export const FULL_HEIGHT = 66;
export const PRESSED_WIDTH = 184;
export const PRESS_SCALE = PRESSED_WIDTH / FULL_WIDTH; // ~0.9388
export const PRESS_DURATION = 150;   // ms — button press-in shrink
export const SPRING_DURATION = 250;  // ms — button spring-back to full size
export const HINT_PRESSED_WIDTH = 172;
export const HINT_PRESS_SCALE = HINT_PRESSED_WIDTH / FULL_WIDTH; // ~0.8776
export const HINT_PRESS_DURATION = 450;  // ms — hint button push-in (3× for debug)

export const HINT_BUTTON_DROP_DURATION = 100; // ms — hint button gravity-drop out
export const HINT_BUTTON_DROP_DELAY = 50;    // ms — stagger after lozenge drop starts
export const PRE_DROP_PAUSE = 100;           // ms — pause between push and drop
export const LOZENGE_DROP_DURATION = 1200;   // ms — clue lozenge drops into cell (slower than button)

export type HintRevealPhase =
  | 'idle'
  | 'pre-pushing'
  | 'pushing'
  | 'pre-dropping'
  | 'pushed-waiting'
  | 'un-pushing'
  | 'dropping'
  | 'post-drop'
  | 'pre-growing'
  | 'growing'
  | 'done';

export function useHintRevealAnimation() {
  const [phase, setPhase] = useState<HintRevealPhase>('idle');
  const waitAfterPushRef = useRef(false);
  const skipPushRef = useRef(false);
  const afterUnPushRef = useRef<'drop' | 'idle'>('idle');

  // start() — full auto-advance (hint reveals, lockout)
  const start = useCallback(() => {
    waitAfterPushRef.current = false;
    setPhase(p => p === 'idle' ? 'pre-pushing' : p);
  }, []);

  // startPush() — push and hold for guess dialog
  const startPush = useCallback(() => {
    waitAfterPushRef.current = true;
    setPhase(p => p === 'idle' ? 'pre-pushing' : p);
  }, []);

  // continueDrop() — skip spring-back, brief pause then drop
  const continueDrop = useCallback(() => {
    setPhase(p => p === 'pushed-waiting' ? 'pre-dropping' : p);
  }, []);

  // startDrop() — skip push, go straight to drop (hint button slide-out)
  const startDrop = useCallback(() => {
    skipPushRef.current = true;
    waitAfterPushRef.current = false;
    setPhase(p => p === 'idle' ? 'pre-pushing' : p);
  }, []);

  // startDropIn() — go from idle to drop (column-solve reveal, no push)
  const startDropIn = useCallback(() => {
    setPhase(p => p === 'idle' ? 'pre-dropping' : p);
  }, []);

  // startWait() — go directly to waiting state (guess dialog, no push animation)
  const startWait = useCallback(() => {
    setPhase(p => p === 'idle' ? 'pushed-waiting' : p);
  }, []);

  // cancelPush() — spring back to idle
  const cancelPush = useCallback(() => {
    afterUnPushRef.current = 'idle';
    setPhase(p => p === 'pushed-waiting' ? 'un-pushing' : p);
  }, []);

  // Auto-advance phases that use timers instead of CSS transitions
  useEffect(() => {
    if (phase === 'pre-pushing') {
      let cancelled = false;
      const skip = skipPushRef.current;
      skipPushRef.current = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setPhase(skip ? 'dropping' : 'pushing');
        });
      });
      return () => { cancelled = true; };
    }
    if (phase === 'pre-dropping') {
      const id = setTimeout(() => setPhase('dropping'), PRE_DROP_PAUSE);
      return () => clearTimeout(id);
    }
    if (phase === 'post-drop') {
      const id = setTimeout(() => setPhase('pre-growing'), POST_DROP_DELAY);
      return () => clearTimeout(id);
    }
    if (phase === 'pre-growing') {
      const id = setTimeout(() => setPhase('growing'), PRE_GROW_DELAY);
      return () => clearTimeout(id);
    }
  }, [phase]);

  // onPushEnd — branches based on waitAfterPushRef
  const onPushEnd = useCallback(() => {
    setPhase(p => {
      if (p !== 'pushing') return p;
      if (waitAfterPushRef.current) {
        waitAfterPushRef.current = false;
        return 'pushed-waiting';
      }
      return 'pre-dropping';
    });
  }, []);

  const onDropEnd = useCallback(() => {
    setPhase(p => p === 'dropping' ? 'post-drop' : p);
  }, []);

  const onGrowEnd = useCallback(() => {
    setPhase(p => p === 'growing' ? 'done' : p);
  }, []);

  // onUnPushEnd — un-pushing → drop or idle based on afterUnPushRef
  const onUnPushEnd = useCallback(() => {
    setPhase(p => {
      if (p !== 'un-pushing') return p;
      if (afterUnPushRef.current === 'drop') {
        skipPushRef.current = true;
        return 'pre-pushing';
      }
      return 'idle';
    });
  }, []);

  // finish — jump straight to idle (skip remaining phases, ready for next animation)
  const finish = useCallback(() => {
    setPhase('idle');
  }, []);

  const isAnimating = phase !== 'idle';

  return {
    phase,
    isAnimating,
    start,
    startDrop,
    startDropIn,
    startPush,
    startWait,
    continueDrop,
    cancelPush,
    onPushEnd,
    onDropEnd,
    onGrowEnd,
    onUnPushEnd,
    finish,
  };
}
